'use client';

import { isComboPack, isDussehraOfferPack, type ProductPack } from '@/lib/productPacks';
import { formatLandingPrice, showLandingGstNote } from '@/lib/formatLandingPrice';
import {
  getComboFeatures,
  getPackConfigKey,
  sortPacksComboCenter,
  useComboPricing,
} from '@/app/landing/hooks/useComboPricing';
import { PaymentRequiredModal } from '@/components/ui/PaymentRequiredModal';
import { PaymentSuccessModal } from '@/components/ui/PaymentSuccessModal';
import { PaymentPendingModal } from '@/components/ui/PaymentPendingModal';
import styles from './LandingPricingSection.module.css';
import cardsConfig from './pricingCardsConfig.json';

// Returns badge label and visual variant from config.
function getBadge(pack: ProductPack): { text: string; variant: 'green' | 'featured' } {
  const { badge } = cardsConfig[getPackConfigKey(pack)];
  return badge as { text: string; variant: 'green' | 'featured' };
}

// ── Component ─────────────────────────────────────────────────────────────────

export function LandingPricingSection() {
  const {
    visible,
    packs,
    selectedPack,
    isModalOpen,
    isProcessing,
    showSuccessModal,
    setShowSuccessModal,
    showPendingModal,
    setShowPendingModal,
    handleBuy,
    handleUnlock,
    handleCancel,
  } = useComboPricing();

  const catalogPacks = packs.filter((pack) => !isDussehraOfferPack(pack));

  if (!visible || catalogPacks.length === 0) return null;

  return (
    <>
      <section className={styles.section} aria-labelledby="pricing-heading">
        <div className={styles.header}>
          <span className={styles.label}>Combo Plans</span>
          <h2 id="pricing-heading" className={styles.heading}>
            Simple, Transparent <span className={styles.headingAccent}>Plans.</span>
          </h2>
          <p className={styles.subheading}>
            Choose the pack that fits your interview preparation goals.
          </p>
        </div>

        <div className={`${styles.grid} ${catalogPacks.length === 2 ? styles.gridTwo : ''}`}>
          {sortPacksComboCenter(catalogPacks).map((pack) => {
            const featured = isComboPack(pack);
            const features = getComboFeatures(pack);
            const badge = getBadge(pack);
            return (
              <article
                key={pack.product_code}
                className={`${styles.card} ${featured ? styles.cardFeatured : ''}`}
              >
                <span
                  className={badge.variant === 'featured' ? styles.badgeFeatured : styles.badgeGreen}
                >
                  {badge.text}
                </span>

                <div className={styles.cardHeader}>
                  <h3 className={styles.productName}>{pack.product_name}</h3>
                  <div>
                    <p className={styles.price}>
                      {formatLandingPrice(pack.display_price, pack.currency)}
                    </p>
                    {showLandingGstNote(pack.currency) ? (
                      <p className={styles.gstNote}>+ GST</p>
                    ) : null}
                  </div>
                </div>

                <ul className={styles.creditsList} aria-label="Included credits">
                  {features.map((line) => (
                    <li key={line} className={styles.creditsItem}>
                      <svg className={styles.checkIcon} width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M7.16667 12.1667L13.0417 6.29167L11.875 5.125L7.16667 9.83333L4.79167 7.45833L3.625 8.625L7.16667 12.1667ZM8.33333 16.6667C7.18056 16.6667 6.09722 16.4479 5.08333 16.0104C4.06944 15.5729 3.1875 14.9792 2.4375 14.2292C1.6875 13.4792 1.09375 12.5972 0.65625 11.5833C0.21875 10.5694 0 9.48611 0 8.33333C0 7.18056 0.21875 6.09722 0.65625 5.08333C1.09375 4.06944 1.6875 3.1875 2.4375 2.4375C3.1875 1.6875 4.06944 1.09375 5.08333 0.65625C6.09722 0.21875 7.18056 0 8.33333 0C9.48611 0 10.5694 0.21875 11.5833 0.65625C12.5972 1.09375 13.4792 1.6875 14.2292 2.4375C14.9792 3.1875 15.5729 4.06944 16.0104 5.08333C16.4479 6.09722 16.6667 7.18056 16.6667 8.33333C16.6667 9.48611 16.4479 10.5694 16.0104 11.5833C15.5729 12.5972 14.9792 13.4792 14.2292 14.2292C13.4792 14.9792 12.5972 15.5729 11.5833 16.0104C10.5694 16.4479 9.48611 16.6667 8.33333 16.6667Z" fill="#34C759" />
                      </svg>
                      {line}
                    </li>
                  ))}
                </ul>

                <div className={styles.cardFooter}>
                  <button
                    className={styles.buyButtonOutline}
                    onClick={() => handleBuy(pack)}
                    type="button"
                    aria-label={`Buy ${pack.product_name} for ${formatLandingPrice(pack.display_price, pack.currency)}`}
                  >
                    Buy Now
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <PaymentRequiredModal
        isOpen={isModalOpen}
        isProcessing={isProcessing}
        productPrice={selectedPack?.price ?? null}
        productName={selectedPack?.product_name ?? null}
        onUnlock={() => void handleUnlock()}
        onCancel={handleCancel}
      />

      {showSuccessModal && (
        <PaymentSuccessModal
          isOpen={showSuccessModal}
          attemptsAdded={1}
          onContinue={() => setShowSuccessModal(false)}
        />
      )}
      <PaymentPendingModal
        isOpen={showPendingModal}
        onClose={() => setShowPendingModal(false)}
      />
    </>
  );
}
